import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Avatar,
  Divider,
  IconButton,
  TextField,
  Stack,
} from "@mui/material";
import {
  Warning as WarningIcon,
  Assignment as AssignmentIcon,
  Info as InfoIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Favorite as FavoriteIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { useAvvisi } from "./AvvisiContext";
import { useTheme } from "@mui/system";
import { useDispatch } from "react-redux";

export const ReadDialog = ({ open, onClose, avviso, isForced = false }) => {
  const { toggleLike, addComment } = useAvvisi();
  const dispatch = useDispatch();
  const [newComment, setNewComment] = useState("");
  const theme = useTheme();

  // Get current user info from session or auth hook if available
  const currentUserId = sessionStorage.getItem("nome");

  if (!avviso) return null;

  // Use Likes array if exists, otherwise empty
  const isLiked =
    Array.isArray(avviso.likes) && avviso.likes.includes(currentUserId);

  const handleLike = () => {
    toggleLike(avviso.idUid);
  };

  const handleSendComment = () => {
    if (newComment.trim()) {
      addComment(avviso.idUid, newComment);
      setNewComment("");
    }
  };

  const handleClose = (event, reason) => {
    // If it's a forced popup, don't allow closing via backdrop or escape
    if (
      isForced &&
      (reason === "backdropClick" || reason === "escapeKeyDown")
    ) {
      return;
    }

    onClose();
  };

  const getTypeConfig = (typeId) => {
    switch (typeId) {
      case 2:
        return {
          icon: <WarningIcon color="error" />,
          color: "#d32f2f",
          bg: "#fdecea",
          label: "Urgente",
        };
      case 3:
        return {
          icon: <AssignmentIcon color="warning" />,
          color: "#ed6c02",
          bg: "#fff4e5",
          label: "Task",
        };
      default:
        return {
          icon: <InfoIcon color="info" />,
          color: "#0288d1",
          bg: "#e1f5fe",
          label: "Info",
        };
    }
  };

  const typeConfig = getTypeConfig(avviso.idTipologia);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            backgroundColor:
              theme.palette.mode === "dark" ? "#1B2635" : "#F7F9FC",
          },
        },
        zIndex: isForced ? 2000 : 1300,
      }}
    >
      <Box
        sx={{
          p: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h3"
            fontWeight="bold"
            sx={{ color: theme.palette.text.primary }}
          >
            {avviso.titolo}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Creato il: {avviso.dataCreazione}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={typeConfig.label}
            icon={typeConfig.icon}
            size="small"
            sx={{
              bgcolor: typeConfig.bg,
              color: typeConfig.color,
              fontWeight: "bold",
            }}
          />
        </Stack>
      </Box>

      <Divider />
      <DialogContent sx={{ height: "auto" }}>
        {/* 
          WARNING: Using dangerouslySetInnerHTML with unsanitized data 
          from the API can expose the application to XSS attacks.
        */}
        <Box
          sx={{
            mb: 3,
            color: theme.palette.text.secondary,
            fontSize: "1.1rem",
            "& h1, & h2, & h3": {
              mt: 2,
              mb: 1,
              color: theme.palette.text.primary,
            },
            "& p": { mb: 1.5, lineHeight: 1.6 },
            "& a": {
              color: theme.palette.primary.main,
              textDecoration: "underline",
            },
            "& ul, & ol": { pl: 3, mb: 1.5 },
            "& li": { mb: 0.5 },
            "& strong": { fontWeight: 700, color: theme.palette.text.primary },
          }}
          dangerouslySetInnerHTML={{ __html: avviso.descrizione || "" }}
        />

        <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
          <Chip
            label={`Letti: ${avviso.avvisiLetti || 0}`}
            size="small"
            variant="outlined"
          />
          <Chip
            label={`Non Letti: ${avviso.avvisiNonLetti || 0}`}
            size="small"
            variant="outlined"
            color="primary"
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Interactions Section */}
        {!isForced && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            {avviso.hasLike == 1 && (
              <Button
                startIcon={
                  isLiked ? (
                    <FavoriteIcon color="error" />
                  ) : (
                    <FavoriteBorderIcon />
                  )
                }
                color={isLiked ? "error" : "primary"}
                onClick={handleLike}
                variant={isLiked ? "outlined" : "text"}
              >
                Like{" "}
                {Array.isArray(avviso.likes) &&
                  avviso.likes.length > 0 &&
                  `(${avviso.likes.length})`}
              </Button>
            )}
          </Box>
        )}

        {/* Comments Section */}
        {avviso.hasComment == 1 && !isForced && (
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: theme.palette.background.default,
              mb: 2,
            }}
          >
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Commenti (
              {Array.isArray(avviso.comments) ? avviso.comments.length : 0})
            </Typography>

            {Array.isArray(avviso.comments) && avviso.comments.length > 0 && (
              <Stack
                spacing={2}
                sx={{ mb: 2, maxHeight: 300, overflowY: "auto", pr: 1 }}
              >
                {avviso.comments.map((comment, idx) => (
                  <Box
                    key={comment.idUid || idx}
                    sx={{ display: "flex", gap: 1.5 }}
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: theme.palette.primary.light,
                      }}
                    >
                      {comment.userName?.charAt(0) || "U"}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: theme.palette.divider,
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mb: 0.5 }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontSize="0.875rem"
                            fontWeight="bold"
                          >
                            {comment.userName || "Utente"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {comment.dataCreazione}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" fontSize="0.875rem">
                          {comment.testo || comment.text}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}

            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Scrivi un commento..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") handleSendComment();
                }}
              />
              <IconButton
                color="primary"
                onClick={handleSendComment}
                disabled={!newComment.trim()}
              >
                <SendIcon />
              </IconButton>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          variant="contained"
          color="primary"
          fullWidth
        >
          {isForced ? "Ho letto e confermo" : "Chiudi"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
